import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, FilterList, DeleteOutline, AddCircleOutline } from '@mui/icons-material';
import api from '../../../services/api';
import styles from '../../../assets/styles/LayoutShared.module.css';
import Button from '../../../components/Button/Button';
import ModalDelete from '../../../components/ModalDelete/ModalDelete';
import InputSearch from '../../../components/InputSearch/InputSearch';

const CATEGORIAS = [
  { value: '', label: 'Todas' },
  { value: 'operacao_agricola', label: '🚜 Operação Agrícola' },
  { value: 'custo',             label: '💸 Custo' },
  { value: 'receita',           label: '💰 Receita' },
  { value: 'manutencao',        label: '🔧 Manutenção' },
  { value: 'abastecimento',     label: '⛽ Abastecimento' },
  { value: 'outros',            label: '📝 Outros' },
];

const STATUS_OPCOES = [
  { value: '', label: 'Todos os status' },
  { value: 'agendado',     label: '🟣 Agendado' },
  { value: 'registrado',   label: '🔵 Registrado' },
  { value: 'em_andamento', label: '🟡 Em andamento' },
  { value: 'concluido',    label: '🟢 Concluído' },
  { value: 'cancelado',    label: '⚫ Cancelado' },
];

const EMOJI_CAT: Record<string, string> = {
  operacao_agricola: '🚜', custo: '💸', receita: '💰',
  manutencao: '🔧', abastecimento: '⛽', outros: '📝',
};

const LABEL_CATEGORIA: Record<string, string> = {
  operacao_agricola: 'Operação Agrícola',
  custo: 'Custo', receita: 'Receita',
  manutencao: 'Manutenção', abastecimento: 'Abastecimento', outros: 'Outros',
};

const formatBRL = (v: number) => v > 0
  ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';

const formatData = (raw: string) => {
  const d = new Date(raw);
  return `${String(d.getUTCDate()).padStart(2,'0')}/${String(d.getUTCMonth()+1).padStart(2,'0')}/${d.getUTCFullYear()}`;
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  agendado:     { label: 'Agendado',     color: '#9b59b6' },
  registrado:   { label: 'Registrado',   color: '#3498db' },
  em_andamento: { label: 'Em andamento', color: '#f39c12' },
  concluido:    { label: 'Concluído',    color: '#00b37e' },
  cancelado:    { label: 'Cancelado',    color: '#999'    },
};

// ─── Hook: detecta se é mobile ────────────────────────────────────────────────
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
};

// ─── Card Mobile ──────────────────────────────────────────────────────────────
const EventoCard = ({
  ev, onNavigate, onDelete,
}: {
  ev: any;
  onNavigate: () => void;
  onDelete: (e: React.MouseEvent) => void;
}) => {
  const valor = parseFloat(ev.valor_total || ev.valor || '0') || 0;
  const isReceita = ev.categoria === 'receita';
  const status = STATUS_CONFIG[ev.status_kanban];

  return (
    <div
      onClick={onNavigate}
      style={{
        background: '#fff',
        borderRadius: 14,
        padding: '14px 16px',
        marginBottom: 10,
        boxShadow: '0 1px 6px rgba(0,0,0,0.07)',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Linha 1: emoji + título + botão delete */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: '1.1rem', flexShrink: 0 }}>{EMOJI_CAT[ev.categoria] || '📝'}</span>
          <span style={{ fontWeight: 700, fontSize: '0.95rem', color: '#222', lineHeight: 1.3, wordBreak: 'break-word' }}>
            {ev.titulo}
          </span>
        </div>
        <Button
          variant='contain'
          onClick={(e) => { e.stopPropagation(); onDelete(e); }}
          typeStyle={["small", "cancel"]}
        >
          <DeleteOutline fontSize="small" />
        </Button>
      </div>

      {/* Linha 2: categoria + área */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <span style={{
          background: '#f0f0f0', color: '#555',
          padding: '2px 10px', borderRadius: 20, fontSize: '0.73rem', fontWeight: 600,
        }}>
          {LABEL_CATEGORIA[ev.categoria] || ev.categoria}
        </span>
        {ev.area_texto && (
          <span style={{
            background: '#e8f5e9', color: '#388e3c',
            padding: '2px 10px', borderRadius: 20, fontSize: '0.73rem', fontWeight: 600,
          }}>
            📍 {ev.area_texto}
          </span>
        )}
      </div>

      {/* Linha 3: valor + data + status */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: '1rem', color: isReceita ? '#00b37e' : '#333' }}>
          {formatBRL(valor)}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: '0.78rem', color: '#999' }}>
            {formatData(ev.data_evento)}
          </span>
          <span style={{
            background: '#f5f5f5', color: status?.color || '#999',
            padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem', fontWeight: 700,
          }}>
            {status?.label || ev.status_kanban}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const AgroEventosList = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [eventos, setEventos] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [temMais, setTemMais] = useState(false);

  const [tempSearch, setTempSearch] = useState('');
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [statusFiltro, setStatusFiltro] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  const [deleteTarget, setDeleteTarget] = useState<{ uid: string; titulo: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(tempSearch); setPagina(1); }, 500);
    return () => clearTimeout(t);
  }, [tempSearch]);

  const fetchEventos = useCallback((page: number, isNew: boolean) => {
    const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
    const params = new URLSearchParams({
      page: String(page), limit: '20',
      ...(search && { search }),
      ...(categoria && { categoria }),
      ...(statusFiltro && { status_kanban: statusFiltro }),
      ...(dataInicio && { dataInicio }),
      ...(dataFim && { dataFim }),
      ...(uidFazenda && { uid_unidade_producao: uidFazenda }),
    });
    api.get(`/agro/eventos?${params}`).then(res => {
      const novos = res.data.data;
      const tot = res.data.total;
      setEventos(prev => isNew ? novos : [...prev, ...novos]);
      setTotal(tot);
      setTemMais((isNew ? novos.length : eventos.length + novos.length) < tot);
    });
  }, [search, categoria, statusFiltro, dataInicio, dataFim]);

  useEffect(() => { setPagina(1); fetchEventos(1, true); }, [search, categoria, statusFiltro, dataInicio, dataFim]);
  useEffect(() => { if (pagina > 1) fetchEventos(pagina, false); }, [pagina]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const uidFazenda = localStorage.getItem('uid_unidade_producao') || '';
      const deleteUrl = `/agro/eventos/${deleteTarget.uid}${uidFazenda ? `?uid_unidade_producao=${uidFazenda}` : ''}`;
      await api.delete(deleteUrl);
      setEventos(prev => prev.filter(e => e.uid_evento !== deleteTarget.uid));
      setTotal(t => t - 1);
    } catch (err) {
      alert('Erro ao excluir o evento. Tente novamente.');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const hasFilters = categoria || statusFiltro || dataInicio || dataFim;

  // ─── Render Mobile ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ background: '#f4f6f8', minHeight: '100vh', paddingBottom: 80 }}>

        {/* Header sticky */}
        <div style={{
          background: '#fff', padding: '14px 16px 10px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          position: 'sticky', top: 0, zIndex: 100,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (showSearch || showFiltros) ? 10 : 0 }}>
            <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 800, color: '#00b37e' }}>
              Histórico de Eventos
            </h1>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ fontSize: '0.8rem', color: '#666' }}>
                <strong style={{ color: '#00b37e' }}>{total}</strong>
              </span>
              <button onClick={() => { setShowSearch(s => !s); if (showSearch) setTempSearch(''); }}
                style={{
                  background: showSearch ? '#e8faf3' : '#f0f0f0', border: 'none', borderRadius: 8,
                  padding: '7px 9px', cursor: 'pointer', color: showSearch ? '#00b37e' : '#555',
                  display: 'flex', alignItems: 'center',
                }}>
                <Search fontSize="small" />
              </button>
              <button onClick={() => setShowFiltros(f => !f)}
                style={{
                  background: (showFiltros || hasFilters) ? '#e8faf3' : '#f0f0f0', border: 'none', borderRadius: 8,
                  padding: '7px 9px', cursor: 'pointer', color: (showFiltros || hasFilters) ? '#00b37e' : '#555',
                  display: 'flex', alignItems: 'center', position: 'relative',
                }}>
                <FilterList fontSize="small" />
                {hasFilters && (
                  <span style={{
                    position: 'absolute', top: 3, right: 3,
                    width: 7, height: 7, borderRadius: '50%',
                    background: '#00b37e', border: '1.5px solid #fff',
                  }} />
                )}
              </button>
              <button onClick={() => navigate('/agro/eventos/novo')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 4,
                  padding: '7px 12px', borderRadius: 8, border: 'none',
                  background: '#00b37e', color: '#fff', cursor: 'pointer',
                  fontWeight: 700, fontSize: '0.82rem',
                }}>
                <AddCircleOutline style={{ fontSize: '1rem' }} />
                Novo
              </button>
            </div>
          </div>

          {showSearch && (
            <div style={{ position: 'relative', marginBottom: showFiltros ? 10 : 0 }}>
              <Search style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#aaa', fontSize: '1.1rem' }} />
              <input
                autoFocus type="text"
                placeholder="Pesquisar por título, área..."
                value={tempSearch}
                onChange={e => setTempSearch(e.target.value)}
                style={{
                  width: '100%', padding: '10px 10px 10px 36px',
                  borderRadius: 10, border: '1px solid #e0e0e0', outline: 'none',
                  fontSize: '0.9rem', boxSizing: 'border-box', background: '#f9f9f9',
                }}
              />
            </div>
          )}

          {showFiltros && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <select value={categoria} onChange={e => { setCategoria(e.target.value); setPagina(1); }}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.82rem', background: '#fafafa' }}>
                  {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
                <select value={statusFiltro} onChange={e => { setStatusFiltro(e.target.value); setPagina(1); }}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.82rem', background: '#fafafa' }}>
                  {STATUS_OPCOES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.82rem' }} />
                <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
                  style={{ flex: 1, padding: '9px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: '0.82rem' }} />
              </div>
              {hasFilters && (
                <button onClick={() => { setCategoria(''); setStatusFiltro(''); setDataInicio(''); setDataFim(''); }}
                  style={{ padding: '9px', borderRadius: 8, border: 'none', background: '#fff0f0', color: '#e74c3c', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem' }}>
                  ✕ Limpar filtros
                </button>
              )}
            </div>
          )}
        </div>

        {/* Lista de cards */}
        <div style={{ padding: '12px 12px 0' }}>
          {eventos.length === 0 && (
            <div style={{ textAlign: 'center', padding: '48px 0', color: '#bbb' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 8 }}>📭</div>
              <p style={{ margin: 0, fontSize: '0.95rem' }}>Nenhum evento encontrado.</p>
            </div>
          )}
          {eventos.map(ev => (
            <EventoCard
              key={ev.uid_evento}
              ev={ev}
              onNavigate={() => navigate(`/agro/eventos/${ev.uid_evento}`)}
              onDelete={() => setDeleteTarget({ uid: ev.uid_evento, titulo: ev.titulo })}
            />
          ))}
        </div>

        {/* Carregar mais */}
        <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0 24px' }}>
          {temMais ? (
            <button onClick={() => setPagina(p => p + 1)}
              style={{
                padding: '12px 32px', borderRadius: 10, border: 'none',
                background: '#00b37e', color: '#fff', cursor: 'pointer',
                fontWeight: 700, fontSize: '0.9rem',
                boxShadow: '0 4px 12px rgba(0,179,126,0.25)',
              }}>
              Carregar mais eventos
            </button>
          ) : (
            <p style={{ color: '#bbb', fontSize: '0.85rem', margin: 0 }}>
              {total > 0 ? `Todos os ${total} eventos carregados` : ''}
            </p>
          )}
        </div>

        <ModalDelete
          isOpen={!!deleteTarget}
          isMobile={isMobile}
          isDeleting={deleting}
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
          entity='evento'
          title={deleteTarget?.titulo || ""}
        />
      </div>
    );
  }

  // ─── Render Desktop (original) ──────────────────────────────────────────────
  return (
    <div className={styles.container}>
      <div className={styles.headerTitle}>
        <h1 className={styles.greenTitle}>Histórico de Eventos</h1>

        <InputSearch
          searchValue={tempSearch}
          setSearchValue={setTempSearch}
          placeholder="Pesquisar por título, área..."
        />

        <div className={styles.actionsHeader}>
          <p style={{ alignSelf: 'center', fontSize: '0.9rem', color: '#666', marginRight: 10 }}>
            Qtd: <strong style={{ color: 'var(--green-700)' }}>{total}</strong>
          </p>
          <button className={styles.blueBtn} onClick={() => setShowFiltros(f => !f)} title="Filtros"
            style={{ background: showFiltros ? '#00b37e' : undefined }}>
            <FilterList />
          </button>
          <button onClick={() => navigate('/agro/eventos/novo')} title="Novo evento"
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: '#00b37e', color: '#fff', cursor: 'pointer',
              fontWeight: 600, fontSize: '0.85rem',
            }}>
            <AddCircleOutline fontSize="small" />
            Novo
          </button>
        </div>
      </div>

      {showFiltros && (
        <div style={{ display: 'flex', gap: 12, padding: '12px 0', flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0', marginBottom: 12 }}>
          <select value={categoria} onChange={e => { setCategoria(e.target.value); setPagina(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', outline: 'none' }}>
            {CATEGORIAS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select value={statusFiltro} onChange={e => { setStatusFiltro(e.target.value); setPagina(1); }}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', outline: 'none' }}>
            {STATUS_OPCOES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <input type="date" value={dataInicio} onChange={e => setDataInicio(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd' }} />
          <input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd' }} />
          {hasFilters && (
            <button onClick={() => { setCategoria(''); setStatusFiltro(''); setDataInicio(''); setDataFim(''); }}
              style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#f75a68', color: '#fff', cursor: 'pointer', fontWeight: 600 }}>
              Limpar
            </button>
          )}
        </div>
      )}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th style={{ width: '32%' }}>Título</th>
              <th>Categoria</th>
              <th>Área</th>
              <th style={{ textAlign: 'right' }}>Valor</th>
              <th style={{ textAlign: 'center' }}>Data</th>
              <th style={{ textAlign: 'center' }}>Status</th>
              <th style={{ textAlign: 'center', width: 48 }}></th>
            </tr>
          </thead>
          <tbody>
            {eventos.length === 0 && (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 30, color: '#999' }}>
                Nenhum evento encontrado.
              </td></tr>
            )}
            {eventos.map(ev => (
              <tr key={ev.uid_evento} style={{ cursor: 'pointer' }}>
                <td onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)}>
                  <span style={{ marginRight: 6 }}>{EMOJI_CAT[ev.categoria] || '📝'}</span>
                  {ev.titulo}
                </td>
                <td onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)} style={{ fontSize: '0.85rem', color: '#666' }}>
                  {ev.categoria?.replace(/_/g, ' ')}
                </td>
                <td onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)} style={{ fontSize: '0.85rem', color: '#888' }}>
                  {ev.area_texto || '—'}
                </td>
                <td onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)} style={{ textAlign: 'right', fontWeight: 600, color: ev.categoria === 'receita' ? '#00b37e' : '#333' }}>
                  {formatBRL(parseFloat(ev.valor_total || ev.valor || '0') || 0)}
                </td>
                <td onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)} style={{ textAlign: 'center', fontSize: '0.85rem' }}>
                  {formatData(ev.data_evento)}
                </td>
                <td onClick={() => navigate(`/agro/eventos/${ev.uid_evento}`)} style={{ textAlign: 'center' }}>
                  <span style={{ background: '#f0f0f0', color: STATUS_CONFIG[ev.status_kanban]?.color || '#999', padding: '3px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600 }}>
                    {STATUS_CONFIG[ev.status_kanban]?.label || ev.status_kanban}
                  </span>
                </td>
                <td style={{ textAlign: 'center' }}>
                  <Button
                    variant='contain'
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget({ uid: ev.uid_evento, titulo: ev.titulo }); }}
                    typeStyle={["small", "cancel"]}
                  >
                    <DeleteOutline fontSize="small" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20, paddingBottom: 20 }}>
        {temMais ? (
          <button className={styles.blueBtn} style={{ width: 'auto', padding: '0 24px' }} onClick={() => setPagina(p => p + 1)}>
            Carregar mais eventos
          </button>
        ) : (
          <p style={{ color: '#999', fontSize: '0.9rem' }}>
            {total > 0 ? `Todos os ${total} eventos carregados` : ''}
          </p>
        )}
      </div>

      <ModalDelete
        isOpen={!!deleteTarget}
        isMobile={isMobile}
        isDeleting={deleting}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        entity='evento'
        title={deleteTarget?.titulo || ""}
      />
    </div>
  );
};

export default AgroEventosList;